import {
  clearPersistedStore,
  makePersistable,
  pausePersisting,
  startPersisting,
} from 'mobx-persist-store'
import { action, computed, observable, reaction, runInAction } from 'mobx'
import {
  compactNumberFormatter,
  compactUsdFormatter,
  numberFormatter,
  usdFormatter,
} from '@/utils/format.js'
import { AFFILIATE_TIERS } from '@/constants/affiliate.js'
import { floor } from 'lodash-es'

export default class AffiliateStore {
  rootStore = null

  #reactions = []

  @observable accessor isUpdating = false
  @observable accessor isError = false

  @observable accessor foundAffiliateId = ''
  @observable accessor totalEarned = 0
  @observable accessor referralCount = 0

  constructor(rootStore) {
    this.rootStore = rootStore
    this.rootStore.registerAfterConstructor(this.#_afterConstructor)
    this.rootStore.registerDeconstructor(this.#_deconstructor)
  }

  #_afterConstructor = async () => {
    await makePersistable(this, {
      name: 'affiliate',
      properties: ['foundAffiliateId'],
    })

    this.#migrate()
    this.#reactions.push(
      reaction(() => this.auth.isLoggedIn, this.#onAuthChanged),
      reaction(() => this.router.search, this.#onSearchChanged)
    )
  }

  #_deconstructor = () => {
    this.#reactions.forEach(x => x())
    this.#reactions = []
  }

  get api() {
    return this.rootStore.apiStore
  }

  get auth() {
    return this.rootStore.authStore
  }

  get router() {
    return this.rootStore.routerStore
  }

  @computed
  get affiliateUrl() {
    return this.auth.isLoggedIn
      ? `${import.meta.env.VITE_APP_FRONTEND_URL}/${this.auth.user.affiliateId}`
      : ''
  }

  @computed
  get totalEarnedFormatted() {
    return usdFormatter.format(this.totalEarned)
  }

  @computed
  get totalEarnedFormattedCompact() {
    return compactUsdFormatter.format(this.totalEarned)
  }

  @computed
  get referralCountFormatted() {
    return numberFormatter.format(this.referralCount)
  }

  @computed
  get referralCountFormattedCompact() {
    return compactNumberFormatter.format(this.referralCount)
  }

  @computed
  get currentTier() {
    return AFFILIATE_TIERS.findLast(x => this.totalEarned >= x.target)
  }

  @computed
  get nextTier() {
    return AFFILIATE_TIERS[this.currentTier.level] ?? this.currentTier
  }

  @computed
  get previousTier() {
    return AFFILIATE_TIERS[this.currentTier.level - 2] ?? this.currentTier
  }

  @computed
  get isLastTier() {
    return this.currentTier.level === this.nextTier.level
  }

  @computed
  get progress() {
    const currentAdjusted = this.totalEarned - this.currentTier.target
    const targetAdjusted = this.nextTier.target - this.currentTier.target
    const percentage = floor((currentAdjusted / targetAdjusted) * 100, 2)

    return Math.max(Math.min(percentage, 100), 0)
  }

  @computed
  get progressFormatted() {
    return `${numberFormatter.format(this.progress)}%`
  }

  update = async () => {
    if (this.isUpdating) return

    runInAction(() => {
      this.isUpdating = true
      this.isError = false
    })

    const response = await this.api.getAffiliate()

    if (response?.data?.status !== 'OK') {
      runInAction(() => {
        this.isUpdating = false
        this.isError = true
      })

      return
    }

    runInAction(() => {
      this.totalEarned = response.data.wallet_unpaid_amount
      this.referralCount = response.data.count_referred
      this.isUpdating = false
    })
  }

  #onAuthChanged = async () => {
    if (this.auth.isLoggedIn) {
      runInAction(() => {
        this.foundAffiliateId = ''
      })

      pausePersisting(this)
      await clearPersistedStore(this)

      return
    }

    startPersisting(this)
  }

  @action
  #onSearchChanged = () => {
    if (this.auth.isLoggedIn) return

    const id = this.router.search.af_id ?? ''

    if (this.foundAffiliateId !== id) {
      this.foundAffiliateId = id
      console.log(`[Affiliate] Found affiliate id: ${this.foundAffiliateId}`)
    }
  }

  @action
  #migrate = () => {
    if (!this.foundAffiliateId) {
      const id = localStorage.getItem('af_id')
      if (id) this.foundAffiliateId = id
    }

    localStorage.removeItem('af_id')
  }
}
